import { ClipboardModule } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CompletionMessage, FileUpload, ToolStreamingStatus } from '@app-libs/model';
import { MarkdownModule, MermaidAPI } from 'ngx-markdown';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { MessageFormatter } from '../../../../services/chat/message/message-formatter.service';
import { StreamContentBufferService } from '../../../../services/chat/streaming/stream-content-buffer.service';
import { StreamProcessor } from '../../../../services/chat/streaming/stream-processor.service';
import { StreamEventType } from '../../../../services/chat/streaming/stream.interfaces';
import { CitationProcessor } from '../../../../services/chat/utils/citation-processor.service';
import { isAssistantMessage, isUserMessage } from '../../../../utils/message-format.utils';
import { FileAttachmentComponent } from '../file-attachment/file-attachment.component';
import { MessageActionsComponent } from '../message-actions/message-actions.component';
import { ToolStatusComponent } from '../tool-status/tool-status.component';


@Component({
  selector: 'app-message-item',
  standalone: true,
  imports: [
    CommonModule,
    MarkdownModule,
    MatTooltipModule,
    ClipboardModule,
    FileAttachmentComponent,
    MessageActionsComponent,
    ToolStatusComponent,
  ],
  templateUrl: './message-item.component.html',
  styleUrls: ['./message-item.component.scss'],
  // Removed OnPush for streaming compatibility
  // changeDetection: ChangeDetectionStrategy.OnPush,
  // Removed animations to prevent messages from disappearing/reappearing during updates
})
export class MessageItemComponent implements OnInit, OnDestroy {
  @Input() message!: CompletionMessage;
  @Input() filteredMessages: CompletionMessage[] = [];
  @Input() activeMessageId: string | null = null;
  @Input() copiedMessageId = '';
  @Input() exportedMessageId = '';
  @Input() toolDisplayMode: 'inline' | 'compact' | 'detail' | 'off' = 'compact';
  @Input() isStreaming = false;

  @Output() copySuccess = new EventEmitter<CompletionMessage>();
  @Output() exportSuccess = new EventEmitter<CompletionMessage>();
  @Output() exportError = new EventEmitter<{ message: CompletionMessage; error: string }>();
  @Output() messageEnter = new EventEmitter<string | null>();
  @Output() messageLeave = new EventEmitter<void>();
  @Output() messageClick = new EventEmitter<CompletionMessage>();
  @Output() messageMouseDown = new EventEmitter<CompletionMessage>();
  @Output() messageMouseUp = new EventEmitter<CompletionMessage>();
  @Output() deleteFile = new EventEmitter<FileUpload>();

  public mermaidOptions: MermaidAPI.MermaidConfig = {
    logLevel: 'fatal',
    suppressErrorRendering: true,
  };

  displayContent = '';
  private destroy$ = new Subject<void>();
  private lastBufferState = { displayContent: '', isComplete: false };

  constructor(
    private messageFormatter: MessageFormatter,
    private streamProcessor: StreamProcessor,
    private citationProcessor: CitationProcessor,
    private streamContentBuffer: StreamContentBufferService
  ) { }

  ngOnInit(): void {
    if (this.isStreaming) {
      // Create a stream of content chunks from the AI response
      const chunks$ = this.streamProcessor.streamEvent$.pipe(
        filter(event => event.type === StreamEventType.CHUNK_RECEIVED),
        map(event => this.citationProcessor.stripCitations(event.data?.content || '')),
        filter(content => content !== '')
      );

      // Process chunks through the buffer service to handle:
      // 1. Incomplete markdown syntax (e.g., **bo -> **bold**)
      // 2. Mermaid diagram buffering (must be complete before rendering)
      // 3. Safe content flushing at word boundaries
      // This prevents visual glitches and rendering errors during streaming
      this.streamContentBuffer.processStream(chunks$).pipe(
        takeUntil(this.destroy$)
      ).subscribe(state => {
        this.displayContent = state.displayContent;
        this.lastBufferState = state;
      });
    }
  }

  ngOnDestroy(): void {
    // Signal completion
    this.destroy$.next();
    this.destroy$.complete();

    // If we were streaming, complete any remaining buffers
    if (this.isStreaming && this.lastBufferState) {
      // The service handles final buffer completion internally
      // We just need to ensure we have the final content
      this.displayContent = this.lastBufferState.displayContent;
    }
  }

  /**
   * Get the content to display - either streaming content or message content
   */
  getDisplayContent(): string {
    return this.isStreaming ? this.displayContent : (this.message?.content || '');
  }

  @HostBinding('class.user')
  get isUser(): boolean {
    return this.message ? isUserMessage(this.message) : false;
  }

  @HostBinding('class.bot')
  get isBot(): boolean {
    return this.message ? isAssistantMessage(this.message) : false;
  }

  @HostBinding('class.message')
  messageClass = true;

  @HostBinding('tabindex')
  tabIndex = 0;

  @HostListener('focus')
  onFocus(): void {
    this.messageEnter.emit(this.message?.message_id || null);
  }

  @HostListener('blur')
  onBlur(): void {
    this.messageLeave.emit();
  }

  @HostListener('click')
  onClick(): void {
    if (this.message) {
      this.messageClick.emit(this.message);
    }
  }

  @HostListener('mousedown')
  onMouseDown(): void {
    if (this.message) {
      this.messageMouseDown.emit(this.message);
    }
  }

  @HostListener('mouseup')
  onMouseUp(): void {
    if (this.message) {
      this.messageMouseUp.emit(this.message);
    }
  }

  onCopySuccess(): void {
    if (this.message) {
      this.copySuccess.emit(this.message);
    }
  }

  onExportSuccess(message: CompletionMessage): void {
    this.exportSuccess.emit(message);
  }

  onExportError(event: { message: CompletionMessage; error: string }): void {
    this.exportError.emit(event);
  }

  onDeleteFile(file: FileUpload): void {
    this.deleteFile.emit(file);
  }

  /**
   * Check if message has math content using MessageFormatter
   */
  get hasMathContent(): boolean {
    return this.messageFormatter.hasMathContent(this.message);
  }

  /**
   * Check if message has code blocks using MessageFormatter
   */
  get hasCodeBlocks(): boolean {
    return this.messageFormatter.hasCodeBlocks(this.message);
  }

  /**
   * Get formatted message for display using MessageFormatter
   */
  get formattedMessage() {
    return this.messageFormatter.formatForDisplay(this.message);
  }

  /**
   * Extract text content from message using MessageFormatter
   */
  get textContent(): string {
    return this.messageFormatter.extractTextContent(this.message);
  }

  /**
   * Get the appropriate tool status for display
   * 
   * Priority:
   * 1. Use streaming_tool_status if present (real-time status during streaming)
   * 2. If tools exist without streaming status, they've been 'used'
   * 2. Default to 'idle' if no tools
   */
  getToolStatus(): ToolStreamingStatus | 'used' {
    // If streaming status is set, use it
    if (this.message?.metadata?.['streaming_tool_status']) {
      return this.message.metadata['streaming_tool_status'];
    }
    // Otherwise, if we have tool calls, they've been used
    if (this.message?.metadata?.tool_calls?.length > 0) {
      return 'used';
    }
    return ToolStreamingStatus.IDLE;
  }
}
